import { ChangeDetectionStrategy, Component } from '@angular/core'
import { AccessState } from 'src/app/services/access.service'
import { StacksetsRegenerationPollService } from 'src/app/shared/services/stacksets-regeneration-poll.service'
import {
  STACKSETS_JOB_IDS,
  STACKSETS_JOB_SCENARIOS,
  STACKSETS_SCENARIO_PRESETS,
  StacksetsJobScenarioKey,
  StacksetsScenarioPreset,
} from '../../mocks/showcase-stacksets-mocks'
import { TestPageMockState } from '../../showcase-mock-state'

/**
 * The StackSets instruction components across every input combination and every
 * regeneration-job status sequence. The job axis lives on the shared state because
 * the patched getJob reads it; the preset axis is local.
 */
@Component({
  selector: 'showcase-stacksets',
  templateUrl: './showcase-stacksets.component.html',
  styleUrls: ['./showcase-stacksets.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShowcaseStacksetsComponent {
  readonly stacksetsJobScenarios = STACKSETS_JOB_SCENARIOS
  readonly stacksetsScenarioPresets = STACKSETS_SCENARIO_PRESETS
  stacksetsScenarioKey: string = STACKSETS_SCENARIO_PRESETS[0].key

  constructor(
    private readonly state: TestPageMockState,
    private readonly regenerationPollService: StacksetsRegenerationPollService
  ) {}

  get stacksetsJobScenario(): StacksetsJobScenarioKey {
    return this.state.stacksetsJobScenario
  }

  get stacksetsActiveJobId(): string | null {
    return STACKSETS_JOB_IDS[this.stacksetsJobScenario]
  }

  get stacksetsJobScenarioIndex(): number {
    return this.stacksetsJobScenarios.findIndex((s) => s.key === this.stacksetsJobScenario)
  }

  setStacksetsJobScenario(key: StacksetsJobScenarioKey): void {
    if (this.state.stacksetsJobScenario === key) return
    this.state.stacksetsJobScenario = key
    // Anchored on the click so timed transitions restart from zero each time.
    this.state.stacksetsScenarioStartMs = Date.now()
    // Distinct jobId per scenario triggers ngOnChanges in the showcased components,
    // which restarts polling — no unmount/remount needed.

    // Shorten the regen poll timeout to 1s for the simulated-timeout scenario so
    // reviewers see the timeout toast without waiting the production 120s.
    if (key === 'running_timeout') {
      this.regenerationPollService.setTimeoutMsForTesting(1000)
    } else {
      this.regenerationPollService.resetTimeoutMsForTesting()
    }
  }

  setStacksetsJobScenarioByKey(key: string): void {
    const scenario = this.stacksetsJobScenarios.find((s) => s.key === key)
    if (scenario) this.setStacksetsJobScenario(scenario.key)
  }

  get stacksetsActivePreset(): StacksetsScenarioPreset {
    return (
      this.stacksetsScenarioPresets.find((p) => p.key === this.stacksetsScenarioKey) ?? this.stacksetsScenarioPresets[0]
    )
  }

  get stacksetsScenarioGroups(): { group: string; presets: StacksetsScenarioPreset[] }[] {
    const groups: { group: string; presets: StacksetsScenarioPreset[] }[] = []
    for (const preset of this.stacksetsScenarioPresets) {
      const existing = groups.find((g) => g.group === preset.group)
      if (existing) existing.presets.push(preset)
      else groups.push({ group: preset.group, presets: [preset] })
    }
    return groups
  }

  setStacksetsScenarioPreset(key: string): void {
    if (this.stacksetsScenarioKey === key) return
    this.stacksetsScenarioKey = key
    // Reset the timed-transition anchor so running_then_done etc. count from the
    // picker change, not from page load.
    this.state.stacksetsScenarioStartMs = Date.now()
  }

  // Convenience getters that fold the active preset's inputs back to plain @Input
  // bindings for the template — avoids spreading complex objects in HTML.
  get s_migrationMode(): boolean {
    return this.stacksetsActivePreset.inputs.migrationMode ?? false
  }
  get s_sourceManagedByStackSet(): boolean {
    return this.stacksetsActivePreset.inputs.sourceManagedByStackSet ?? false
  }
  get s_targetManagedByStackSet(): boolean {
    return this.stacksetsActivePreset.inputs.targetManagedByStackSet ?? false
  }
  get s_sourceStackName(): string {
    return this.stacksetsActivePreset.inputs.sourceStackName ?? ''
  }
  get s_targetStackName(): string {
    return this.stacksetsActivePreset.inputs.targetStackName ?? ''
  }

  // Flipped true briefly when the user clicks RECHECK on the CF-conflict banner so
  // the existing test-access spinner kicks in and the click is observable.
  private recheckSourceOverride = false
  private recheckTargetOverride = false

  get s_testingSource(): boolean {
    return this.recheckSourceOverride || (this.stacksetsActivePreset.inputs.testingSource ?? false)
  }
  get s_testingTarget(): boolean {
    return this.recheckTargetOverride || (this.stacksetsActivePreset.inputs.testingTarget ?? false)
  }

  onStacksetsTestAccess(scope: 'source' | 'target'): void {
    if (scope === 'source') {
      this.recheckSourceOverride = true
      setTimeout(() => (this.recheckSourceOverride = false), 1200)
    } else {
      this.recheckTargetOverride = true
      setTimeout(() => (this.recheckTargetOverride = false), 1200)
    }
  }

  get s_sourceError(): boolean {
    return this.stacksetsActivePreset.inputs.sourceError ?? false
  }
  get s_targetError(): boolean {
    return this.stacksetsActivePreset.inputs.targetError ?? false
  }
  get s_sourceTestCount(): number {
    return this.stacksetsActivePreset.inputs.sourceTestCount ?? 0
  }
  get s_targetTestCount(): number {
    return this.stacksetsActivePreset.inputs.targetTestCount ?? 0
  }
  get s_sourceAccessState(): AccessState {
    return this.stacksetsActivePreset.inputs.sourceAccessState ?? AccessState.UPDATE_TEMPLATE
  }
  get s_targetAccessState(): AccessState {
    return this.stacksetsActivePreset.inputs.targetAccessState ?? AccessState.UPDATE_TEMPLATE
  }
}
